import {
    ReparoRequestMethodHeader,
    RequestMethod,
    type AnyType,
    type ApiError,
    type ApiResponse,
} from '@diagon-alley/common';
import { spanToTraceHeader, startSpan } from '@sentry/react';
import fetch from 'isomorphic-fetch';

// =============================================================================
// RE-EXPORT AUTH UTILITIES
// =============================================================================
export {
    getAuthToken,
    getMerchantId,
    clearMerchantIdCache,
    getAuthHeaders,
    getApiBase,
    login,
    signup,
    type LoginParams,
    type SignupParams,
    type AuthResponse,
} from './auth';

const DEFAULT_PROD_API_URL = 'https://api.reparo.co.in';
const normalizedProdApiUrl = (
    import.meta.env.VITE_API_BASE_URL || DEFAULT_PROD_API_URL
).replace(/\/+$/, '');

// In development, keep API base relative so Vite proxy handles /api requests.
export const BASE_API_URL = import.meta.env.DEV ? '' : normalizedProdApiUrl;

const NETWORK_ERROR_MESSAGE =
    'Could not connect to Reparo server. Please verify your network connection and server configuration.';

/**
 * Normalises whatever an error response carried into the ApiError shape the
 * app expects. The load-bearing case is a NestJS error body
 * ({ statusCode, message, error }): it must surface the real status and
 * message rather than collapsing every backend 4xx/5xx into a generic
 * "could not connect", which hides validation failures from the user.
 */
const handleError = (err: any): ApiError => {
    // Already a fully-formed ApiError ({ status: 'error', error: { ... } }).
    if (err?.error?.statusCode && err?.error?.name) {
        return err as ApiError;
    }

    // NestJS HttpException body: { statusCode, message, error }. `message` is
    // a string for most exceptions; for class-validator (DTO) failures it is an
    // array whose entries are strings OR ValidationError OBJECTS (the strict
    // pipe's exceptionFactory passes them raw) — joining objects directly
    // printed "[object Object]" at the user. Flatten to the constraint texts.
    if (err && typeof err.statusCode === 'number') {
        const flattenValidationEntry = (entry: any): string => {
            if (typeof entry === 'string') return entry;
            if (entry && typeof entry === 'object') {
                const own = entry.constraints
                    ? Object.values(entry.constraints as Record<string, string>)
                    : [];
                const nested = Array.isArray(entry.children)
                    ? entry.children.map(flattenValidationEntry).filter(Boolean)
                    : [];
                const parts = [...own, ...nested];
                if (parts.length > 0) {
                    return entry.property
                        ? `${entry.property}: ${parts.join('; ')}`
                        : parts.join('; ');
                }
            }
            try {
                return JSON.stringify(entry);
            } catch {
                return String(entry);
            }
        };
        const rawMessage = Array.isArray(err.message)
            ? err.message.map(flattenValidationEntry).join('; ')
            : err.message;
        return {
            status: 'error',
            error: {
                name: typeof err.error === 'string' ? err.error : 'RequestError',
                statusCode: err.statusCode,
                message: String(rawMessage ?? err.error ?? 'Request failed'),
                data: err,
            },
        };
    }

    // Non-JSON HTTP error (gateway error page, empty body) — surface the
    // status code even though there is no structured message.
    if (err && typeof err.status === 'number') {
        return {
            status: 'error',
            error: {
                name: 'HttpError',
                statusCode: err.status,
                message: `Request failed (${err.status} ${err.statusText ?? ''})`.trim(),
                data: err,
            },
        };
    }

    // No usable error body — a genuine connection failure.
    return {
        status: 'error',
        error: {
            name: 'NetworkError',
            statusCode: 500,
            message: NETWORK_ERROR_MESSAGE,
            data: err,
        },
    };
};

type ReparoApiProps = {
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
    url: string;
    body?: BodyInit | null;
    headers?: Record<string, string> | undefined | null;
    version?: 'v1' | 'v2';
    stream?: boolean;
    /**
     * Coalesce this call with an identical one already in flight, sharing a
     * single round trip. Read-only calls only — several screens issue the same
     * expensive aggregate from independent effects on the same render.
     *
     * Opt-in because reads here go over POST: coalescing by default would drop
     * a genuine second submission of a write. Callers share the same resolved
     * object, so treat the result as immutable.
     */
    dedupe?: boolean;
};

const executeRequest = async <T>({
    method,
    url,
    body,
    headers,
    version = 'v1',
    stream = false,
}: ReparoApiProps): Promise<T> => {
    const apiPrefix = BASE_API_URL
        ? `${BASE_API_URL}/api/${version}`
        : `/api/${version}`;
    let sentryTrace: string | undefined;
    const isFormData =
        typeof FormData !== 'undefined' && body instanceof FormData;

    const defaultHeaders = isFormData
        ? { [ReparoRequestMethodHeader]: RequestMethod.WEB_APP }
        : {
              'Content-Type': 'application/json',
              [ReparoRequestMethodHeader]: RequestMethod.WEB_APP,
          };

    const authToken = localStorage.getItem('token');
    // Manually create a span for the fetch request to be able to trace it in Sentry. This also enables Distributed Tracing.
    startSpan(
        {
            op: 'http.client',
            name: `API Request: ${method} ${url}`,
            attributes: {
                'http.method': method,
                'http.url': url,
                type: 'fetch',
                url,
                method,
            },
        },
        (s) => {
            sentryTrace = spanToTraceHeader(s);
        },
    );

    const finalHeaders =
        headers === null
            ? {}
            : {
                  ...defaultHeaders,
                  ...headers,
                  ...(sentryTrace ? { 'sentry-trace': sentryTrace } : {}),
              };

    const response = await fetch(`${apiPrefix}${url}`, {
        method,
        headers: {
            ...finalHeaders,
            Authorization: `Bearer ${authToken}`,
            // OR if your API needs a raw token:
            // Authorization: token
        },
        body,
    });

    if (!response.ok) {
        if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        // Error responses don't always carry a JSON body (e.g. backend down,
        // gateway 5xx, empty error). Parse defensively so we fall through to
        // handleError's friendly NetworkError instead of throwing a raw
        // "Unexpected end of JSON input" SyntaxError.
        const rawErrorBody = await response.text();
        let errorData: AnyType;
        try {
            errorData = rawErrorBody ? JSON.parse(rawErrorBody) : null;
        } catch {
            errorData = null;
        }
        if (!errorData || typeof errorData !== 'object') {
            errorData = {
                status: response.status,
                statusText: response.statusText,
                body: rawErrorBody,
            };
        }
        throw handleError(errorData);
    }

    if (stream) {
        if (!response.body) {
            throw new Error('ReadableStream not supported in this browser.');
        }
        return response.body as T;
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T;
    }

    return response.json().then((d: ApiResponse | ApiError | any) => {
        if (Array.isArray(d)) {
            return d as T;
        }

        if (d && typeof d === 'object' && 'status' in d) {
            if (d.status === 'ok') {
                // make sure we return null instead of undefined
                // otherwise react-query will crash
                return ((d as ApiResponse).results ?? null) as T;
            }
            if (d.status === 'error') {
                throw d as ApiError;
            }
            // For any other status value (e.g. domain entities with a status field
            // like RebalancePlanDto.status = 'DRAFT'), fall through and return as-is.
        }

        // Fallback: return as-is (some endpoints return raw JSON)
        return d as T;
    });
};

/** Keyed by method+url+body. Concurrent identical calls share one round trip,
 *  and a successful result lingers for DEDUPE_GRACE_MS so near-miss duplicates
 *  (two effects firing the same body a beat apart — e.g. the Sales page's main
 *  and card fetches at default scope) also reuse it. Failures are evicted
 *  immediately so a retry always re-hits the server. Only opt-in (dedupe:true)
 *  read-style calls ever enter this map. */
const DEDUPE_GRACE_MS = 3000;
const inFlightRequests = new Map<string, Promise<unknown>>();

export const ReparoApi = <T>(props: ReparoApiProps): Promise<T> => {
    if (!props.dedupe || props.stream) return executeRequest<T>(props);

    const key = `${props.method} ${props.url} ${
        typeof props.body === 'string' ? props.body : ''
    }`;
    const existing = inFlightRequests.get(key);
    if (existing) return existing as Promise<T>;

    const pending = executeRequest<T>(props);
    inFlightRequests.set(key, pending);
    // Settle handler on both paths, so a rejection clears the entry without
    // creating an unhandled derived rejection.
    const clear = () => inFlightRequests.delete(key);
    pending.then(() => setTimeout(clear, DEDUPE_GRACE_MS), clear);
    return pending;
};

/**
 * Fetches a stream from the given URL and processes each chunk.
 * @param url - The URL to fetch the stream from.
 * @param onChunk - Callback function to handle each chunk.
 * @param onComplete - Callback function to handle the completion of the stream.
 * @param onError - Callback function to handle errors.
 */
export async function handleStream(
    url: string,
    onChunk: (chunk: AnyType) => void,
    onComplete: () => void,
    onError: (error: unknown) => void,
    body: AnyType,
    method: 'GET' | 'POST' = 'GET',
): Promise<void> {
    try {
        const stream = await ReparoApi<ReadableStream<Uint8Array>>({
            method,
            url,
            body: JSON.stringify(body),
            stream: true,
        });

        const reader = stream.getReader();
        const decoder = new TextDecoder('utf-8');

        let buffer = '';
        let done = false;
        while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;
            if (value) {
                buffer = decoder.decode(value, { stream: true });
                const chunk = JSON.parse(buffer);
                onChunk(chunk);
            }
        }

        onComplete();
    } catch (error) {
        onError(error);
    }
}
